import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { Loading } from 'carbon-components-react';
import { createCheckoutSessionStart } from 'redux/payment/actions';
import {
  selectCheckoutSessionSecret,
  selectCheckingOut,
  selectPaymentError
} from 'redux/payment/selectors';
import { CreateCheckoutSessionPayload } from 'types';
import './index.scss';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

const StripeEmbeddedCheckout: React.FC<Props> = ({
  paymentType,
  returnPath,
  quantity,
  metaData
}) => {
  const dispatch = useDispatch();
  const checkoutSessionSecret = useSelector(selectCheckoutSessionSecret);
  const checkingOut = useSelector(selectCheckingOut);
  const error = useSelector(selectPaymentError);

  useEffect(() => {
    const payload: CreateCheckoutSessionPayload = {
      paymentType,
      returnUrl: `${window.location.origin}${returnPath}?session_id={CHECKOUT_SESSION_ID}`,
      quantity: quantity || undefined,
      metaData: metaData || undefined
    };
    dispatch(createCheckoutSessionStart(payload));
  }, [dispatch, paymentType, returnPath, quantity, metaData]);

  if (error) {
    return (
      <div className="stripe-embedded-checkout stripe-embedded-checkout--error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (checkingOut) {
    return (
      <div className="stripe-embedded-checkout stripe-embedded-checkout--loading">
        <Loading withOverlay={false} />
      </div>
    );
  }

  return (
    <div className="stripe-embedded-checkout">
      {checkoutSessionSecret && (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret: checkoutSessionSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </div>
  );
};

const propTypes = {
  paymentType: PropTypes.string.isRequired,
  returnPath: PropTypes.string.isRequired,
  quantity: PropTypes.number,
  metaData: PropTypes.object
};

type Props = PropTypes.InferProps<typeof propTypes>;

StripeEmbeddedCheckout.propTypes = propTypes;

export default StripeEmbeddedCheckout;
